import { useState } from "react"
import {ChevronDown, HelpCircle} from 'lucide-react'

const faqs = [
  {
    question: "How much fat per day should I eat for weight loss?",
    answer:
      "For weight loss, most nutrition experts recommend getting 20-35% of your total daily calories from fat. If you are on a calorie-restricted diet (e.g., 1,600 kcal/day), that translates to roughly 36-62 grams of fat per day. Our fat intake calculator automatically adjusts for weight loss by reducing your TDEE by 20%, then calculating the optimal fat range. Focus on healthy fats from sources like avocados, nuts, olive oil, and fatty fish to stay satiated while in a caloric deficit.",
  },
  {
    question: "How much water should I drink daily to lose weight?",
    answer:
      "A common guideline is to drink at least 35ml of water per kilogram of body weight, plus an additional 500ml for every 30 minutes of exercise. For a 75kg person exercising 30 minutes daily, that is approximately 3.1 liters (about 12 glasses). Staying well-hydrated boosts your metabolism by up to 30%, helps suppress appetite, and improves your body's ability to metabolize stored fat. Use our water intake calculator above for a personalized recommendation.",
  },
  {
    question: "How many calories should I eat to maintain weight?",
    answer:
      "To maintain your current weight, you need to consume calories equal to your Total Daily Energy Expenditure (TDEE). TDEE is calculated by multiplying your Basal Metabolic Rate (BMR) by an activity factor. For example, a moderately active 30-year-old male weighing 80kg and standing 180cm tall has a TDEE of approximately 2,600 kcal. Our calculator uses the Mifflin-St Jeor equation — the most accurate BMR formula — to determine your maintenance calories.",
  },
  {
    question: "What is the Mifflin-St Jeor equation?",
    answer:
      "The Mifflin-St Jeor equation is considered the most accurate formula for estimating Basal Metabolic Rate (BMR). For men: BMR = (10 x weight in kg) + (6.25 x height in cm) - (5 x age) + 5. For women: BMR = (10 x weight in kg) + (6.25 x height in cm) - (5 x age) - 161. This equation was developed in 1990 and has been validated in numerous studies as more accurate than the older Harris-Benedict equation.",
  },
  {
    question: "Is this fat intake calculator suitable for bodybuilding?",
    answer:
      "Yes! Our fat intake calculator for bodybuilding works by adjusting your caloric needs based on your goal. For muscle gain, we increase your TDEE by 15% to create a caloric surplus. Bodybuilders typically aim for 25-30% of calories from fat to support testosterone production and recovery. The calculator provides a 20-35% range so you can fine-tune based on your specific macro split (e.g., high-protein, moderate-fat, moderate-carb for lean bulking).",
  },
  {
    question: "What are the best sources of healthy fats?",
    answer:
      "The best sources of healthy fats include: Monounsaturated fats (avocados, olive oil, almonds, cashews), Polyunsaturated fats including Omega-3s (salmon, sardines, walnuts, flaxseeds, chia seeds), and moderate amounts of saturated fat from whole foods (eggs, dark chocolate, coconut oil). Avoid trans fats found in processed foods, margarine, and fried foods. A balanced approach to fat intake supports heart health, brain function, and hormonal balance.",
  },
]

const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map((faq) => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer,
    },
  })),
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  return (
    <section id="faq" className="py-16 sm:py-20 bg-gray-50 noise-bg">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 mb-4">
            <HelpCircle className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">FAQ</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">
            Frequently Asked Questions
          </h2>
          <p className="text-slate-500">
            Everything you need to know about calculating your daily fat intake.
          </p>
        </div>

        {/* Accordion */}
        <div className="space-y-3">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index
            return (
              <div
                key={index}
                className={`bg-white rounded-xl border transition-all duration-300 ${
                  isOpen ? "border-emerald-200 shadow-md" : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-4 text-left"
                  aria-expanded={isOpen}
                >
                  <span className={`text-sm sm:text-base font-semibold transition-colors ${isOpen ? "text-emerald-700" : "text-slate-700"}`}>
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${
                      isOpen ? "rotate-180 text-emerald-500" : "text-gray-400"
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 sm:px-6 pb-5 animate-slide-down">
                    <p className="text-sm text-slate-600 leading-relaxed border-t border-gray-100 pt-4">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
